import { useState, useMemo } from 'react';
import { Card, Col, Row, Button, Modal } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactSelect from 'react-select';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import SoftBadge from 'components/common/SoftBadge';
import Flex from 'components/common/Flex';
import Avatar from 'components/common/Avatar';
import LoadingContainer from 'components/common/LoadingContainer';
import {
  useDonorsWithRemainingDonations,
  useSponsorshipsForManagement
} from 'hooks/useBeneficiaries';
import { formatMoney } from 'helpers/utils';
import paths from 'routes/paths';

// Stats Card Component
const StatCard = ({ icon, title, value, subtitle, color = 'primary' }) => (
  <Card className="h-100">
    <Card.Body>
      <Flex justifyContent="between" alignItems="center">
        <div>
          <h6 className="text-700 mb-1">{title}</h6>
          <h2 className={`text-${color} mb-0`}>{value}</h2>
          {subtitle && <p className="text-500 fs--1 mb-0 mt-1">{subtitle}</p>}
        </div>
        <div className={`icon-circle icon-circle-${color}`}>
          <FontAwesomeIcon icon={icon} className="fs-2" />
        </div>
      </Flex>
    </Card.Body>
  </Card>
);

// Simple Beneficiary Selection Modal
const SelectBeneficiaryModal = ({
  show,
  onHide,
  donation,
  beneficiaries,
  onSelect
}) => {
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  // Group beneficiaries by beneficiaryId (data is now flattened by sponsorship type)
  // and aggregate their sponsorship types
  const groupedBeneficiaries = useMemo(() => {
    const groups = {};
    (beneficiaries || []).forEach(b => {
      if (!groups[b.beneficiaryId]) {
        groups[b.beneficiaryId] = {
          id: b.beneficiaryId,
          name: b.name,
          country: b.country,
          simpleId: b.simpleId,
          sponsorshipTypes: [],
          totalMonthlyCost: 0,
          minCoverage: 100 // Track minimum coverage across types
        };
      }
      groups[b.beneficiaryId].sponsorshipTypes.push({
        type: b.sponsorshipType,
        coverage: b.totalCoverage,
        cost: b.monthlyCost
      });
      groups[b.beneficiaryId].totalMonthlyCost += b.monthlyCost || 0;
      groups[b.beneficiaryId].minCoverage = Math.min(
        groups[b.beneficiaryId].minCoverage,
        b.totalCoverage
      );
    });
    return Object.values(groups);
  }, [beneficiaries]);

  // Filter beneficiaries that need sponsorship (have at least one type < 100% covered)
  const eligibleBeneficiaries = groupedBeneficiaries.filter(
    b => b.minCoverage < 100 && b.sponsorshipTypes.length > 0
  );

  // Sort by priority (lowest coverage first)
  const sortedBeneficiaries = [...eligibleBeneficiaries].sort((a, b) => {
    return a.minCoverage - b.minCoverage;
  });

  // ReactSelect options
  const beneficiaryOptions = sortedBeneficiaries.map(ben => ({
    value: ben.id,
    label: `${ben.name} - ${ben.sponsorshipTypes.length} types (${formatMoney(ben.totalMonthlyCost)}/mo)`,
    beneficiary: ben
  }));

  const handleContinue = () => {
    if (selectedBeneficiary) {
      onSelect(selectedBeneficiary.beneficiary.id);
      setSelectedBeneficiary(null);
    }
  };

  // Guard AFTER all hooks so hook order stays consistent across renders
  // (moving this above the hooks violated the Rules of Hooks and crashed the
  // modal when a donation was selected).
  if (!donation) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Select Beneficiary</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Donation Info */}
        <Card className="bg-light mb-3">
          <Card.Body className="py-2">
            <Flex justifyContent="between" alignItems="center">
              <div>
                <p className="mb-0 fw-medium">
                  {donation.donor.first_name} {donation.donor.last_name}
                </p>
                <p className="mb-0 text-500 fs--1">
                  {donation.donationType || 'General Donation'}
                </p>
              </div>
              <div className="text-end">
                <p className="mb-0 fw-bold text-success">
                  {formatMoney(donation.remainingAmount)}
                </p>
                <p className="mb-0 text-500 fs--1">available</p>
              </div>
            </Flex>
          </Card.Body>
        </Card>

        {/* Beneficiary Selection */}
        <ReactSelect
          value={selectedBeneficiary}
          onChange={option => setSelectedBeneficiary(option)}
          options={beneficiaryOptions}
          placeholder="Select beneficiary to sponsor..."
          classNamePrefix="react-select"
          isClearable
        />
        {sortedBeneficiaries.length === 0 && (
          <small className="text-warning d-block mt-2">
            No beneficiaries need sponsorship or have sponsorship types defined.
          </small>
        )}

        {/* Selected Beneficiary Preview */}
        {selectedBeneficiary && (
          <Card className="border mt-3">
            <Card.Body className="py-2">
              <Flex alignItems="center">
                <Avatar
                  size="2xl"
                  name={selectedBeneficiary.beneficiary.name}
                  className="me-2"
                />
                <div>
                  <p className="mb-0 fw-medium">
                    {selectedBeneficiary.beneficiary.name}
                  </p>
                  <p className="mb-0 text-600 fs--1">
                    {selectedBeneficiary.beneficiary.country} |{' '}
                    Min coverage: {selectedBeneficiary.beneficiary.minCoverage}%
                  </p>
                  <p className="mb-0 text-500 fs--1">
                    {selectedBeneficiary.beneficiary.sponsorshipTypes?.length || 0} sponsorship types
                  </p>
                </div>
              </Flex>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={!selectedBeneficiary}
        >
          Continue to Sponsorship Form
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

function SponsorshipDonation() {
  const navigate = useNavigate();
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [showSelectModal, setShowSelectModal] = useState(false);

  // Fetch donations with remaining balance
  const { donorsWithDonations, isLoading: loadingDonations } =
    useDonorsWithRemainingDonations();

  // Fetch beneficiaries for selection
  const { sponsorships: beneficiaries, isLoading: loadingBeneficiaries } =
    useSponsorshipsForManagement();

  const isLoading = loadingDonations || loadingBeneficiaries;

  // Show all donations with remaining balance for sponsorship assignment
  // Add id field for react-table row identification
  const sponsorshipDonations = useMemo(() => {
    return donorsWithDonations.map(d => ({
      ...d,
      id: d.orderDetailId
    }));
  }, [donorsWithDonations]);

  // Group beneficiaries by ID (data is flattened by sponsorship type)
  const groupedBeneficiaries = useMemo(() => {
    const groups = {};
    beneficiaries.forEach(b => {
      if (!groups[b.beneficiaryId]) {
        groups[b.beneficiaryId] = {
          id: b.beneficiaryId,
          name: b.name,
          country: b.country,
          minCoverage: 100
        };
      }
      groups[b.beneficiaryId].minCoverage = Math.min(
        groups[b.beneficiaryId].minCoverage,
        b.totalCoverage
      );
    });
    return Object.values(groups);
  }, [beneficiaries]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalPending = sponsorshipDonations.length;
    const totalAmount = sponsorshipDonations.reduce(
      (sum, d) => sum + d.remainingAmount,
      0
    );
    // Count beneficiaries with at least one unsponsored type
    const urgentCount = groupedBeneficiaries.filter(b => b.minCoverage === 0).length;

    return { totalPending, totalAmount, urgentCount };
  }, [sponsorshipDonations, groupedBeneficiaries]);

  // Handle beneficiary selection - navigate to CreateSponsorship, carrying the
  // donation the user clicked "Assign" on so the form lands prefilled (donor +
  // sponsorship type). Without this the donation-first worklist would dump the
  // user at a blank form and they'd have to re-find the same donation.
  const handleBeneficiarySelect = beneficiaryId => {
    const orderDetailId = selectedDonation?.orderDetailId;
    setShowSelectModal(false);
    setSelectedDonation(null);
    const params = new URLSearchParams({ beneficiaryId });
    if (orderDetailId) params.set('orderDetailId', orderDetailId);
    navigate(`${paths.createNewSponsorship}?${params.toString()}`);
  };

  if (isLoading) return <LoadingContainer />;

  const tableColumns = [
    {
      id: 'donor',
      // Return a searchable string (name + ref) so the global search matches on
      // donor name. A plain `accessor: 'donor'` exposes the donor object, which
      // react-table stringifies to "[object Object]" and can never be searched.
      accessor: row =>
        `${row.donor?.first_name || ''} ${row.donor?.last_name || ''} #${
          row.donor?.simple_id || ''
        }`,
      Header: 'Donor',
      cellProps: { className: 'py-2' },
      Cell: ({ row }) => {
        const { donor } = row.original;
        return (
          <Flex alignItems="center">
            <Avatar size="l" name={`${donor.first_name} ${donor.last_name}`} />
            <div className="ms-2">
              <Link
                to={paths.donorDetails.replace(':id', donor.id)}
                className="mb-0 fw-medium"
              >
                {donor.first_name} {donor.last_name}
              </Link>
              <p className="mb-0 text-500 fs--1">#{donor.simple_id}</p>
            </div>
          </Flex>
        );
      }
    },
    {
      accessor: 'createdAt',
      Header: 'Date',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) => value ? dayjs(value).format('DD/MM/YYYY') : '-'
    },
    {
      accessor: 'sponsorshipType',
      Header: 'Sponsorship Type',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) => value || '-'
    },
    {
      accessor: 'donationType',
      Header: 'Donation Type',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) => value || 'General'
    },
    {
      accessor: 'billingCondition',
      Header: 'Frequency',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) => (
        <SoftBadge bg={value === 'monthly' ? 'primary' : 'secondary'}>
          {value || 'One-time'}
        </SoftBadge>
      )
    },
    {
      accessor: 'amount',
      Header: 'Amount',
      cellProps: { className: 'py-2' },
      Cell: ({ value }) => formatMoney(value)
    },
    {
      accessor: 'suggestedBeneficiary',
      Header: 'Suggested Match',
      cellProps: { className: 'py-2' },
      Cell: () => {
        // Find best match from grouped beneficiaries (most urgent need)
        const urgentBeneficiary = groupedBeneficiaries.find(
          b => b.minCoverage === 0
        );
        const partialBeneficiary = groupedBeneficiaries.find(
          b => b.minCoverage > 0 && b.minCoverage < 100
        );
        const suggested = urgentBeneficiary || partialBeneficiary;

        if (!suggested) return <span className="text-500">No match</span>;

        return (
          <div>
            <p className="mb-0 fw-medium">{suggested.name}</p>
            <p className="mb-0 text-500 fs--1">
              {suggested.minCoverage}% covered
            </p>
          </div>
        );
      }
    },
    {
      accessor: 'priority',
      Header: 'Priority',
      cellProps: { className: 'py-2' },
      Cell: ({ row }) => {
        const amount = row.original.remainingAmount;
        if (amount >= 500) return <SoftBadge bg="danger">High</SoftBadge>;
        if (amount >= 200) return <SoftBadge bg="warning">Medium</SoftBadge>;
        return <SoftBadge bg="info">Normal</SoftBadge>;
      }
    },
    {
      accessor: 'actions',
      Header: '',
      cellProps: { className: 'py-2 text-end' },
      disableSortBy: true,
      Cell: ({ row }) => (
        <Button
          variant="success"
          size="sm"
          onClick={() => {
            setSelectedDonation(row.original);
            setShowSelectModal(true);
          }}
        >
          <FontAwesomeIcon icon="check" className="me-1" />
          Assign
        </Button>
      )
    }
  ];

  return (
    <>
      {/* Stats Cards */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <StatCard
            icon="donate"
            title="Pending Donations"
            value={stats.totalPending}
            subtitle="Awaiting assignment"
            color="primary"
          />
        </Col>
        <Col md={4}>
          <StatCard
            icon="dollar-sign"
            title="Total Available"
            value={formatMoney(stats.totalAmount)}
            subtitle="Ready to allocate"
            color="success"
          />
        </Col>
        <Col md={4}>
          <StatCard
            icon="exclamation-triangle"
            title="Urgent Needs"
            value={stats.urgentCount}
            subtitle="Unsponsored beneficiaries"
            color="danger"
          />
        </Col>
      </Row>

      {/* Main Table */}
      <Row>
        <Col>
          <CustomAdvanceTable
            key={`donations-${sponsorshipDonations.length}`}
            data={sponsorshipDonations}
            columns={tableColumns}
            loading={isLoading}
            title="Sponsorship Donation Review"
            showSearchInput
            searchInputPlaceHolder="Search by donor name..."
            perPage={50}
            showTablePagination
          />
        </Col>
      </Row>

      {/* Empty State */}
      {sponsorshipDonations.length === 0 && !isLoading && (
        <Card className="mt-3">
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon
              icon="check-circle"
              className="text-success fs-3 mb-3"
            />
            <h5>All Caught Up!</h5>
            <p className="text-600 mb-0">
              No donations are waiting to be assigned to beneficiaries.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Select Beneficiary Modal */}
      <SelectBeneficiaryModal
        show={showSelectModal}
        onHide={() => {
          setShowSelectModal(false);
          setSelectedDonation(null);
        }}
        donation={selectedDonation}
        beneficiaries={beneficiaries}
        onSelect={handleBeneficiarySelect}
      />

      <style>{`
        .icon-circle {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.15;
        }
        .icon-circle-primary { background-color: var(--falcon-primary); }
        .icon-circle-success { background-color: var(--falcon-success); }
        .icon-circle-danger { background-color: var(--falcon-danger); }
      `}</style>
    </>
  );
}

export default SponsorshipDonation;
